// ═══════════════════════════════════════════════════════════════════════════════
// Task group wizard validation schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { MAX_MEDICATION_REMINDER_SLOTS } from '../constants/index.js';
import { optionalTimeSchema } from './common.schema.js';
import { monthlyKindSchema, recurrenceDaysSchema, recurrenceMonthDaysSchema, recurrenceOrdinalSchema, recurrenceTypeSchema, refineOccurrenceOrStartDates, refineWeekdaySelection, } from './recurrence.schema.js';
import { dateSchema } from './common.schema.js';
const taskDateRangeSchema = z
    .object({
    startDate: dateSchema,
    endDate: dateSchema,
})
    .refine((data) => data.startDate <= data.endDate, {
    message: 'Range end date must be on or after start date',
    path: ['endDate'],
});
const scheduleFieldsSchema = z.object({
    description: z
        .string()
        .max(1000)
        .default('')
        .transform((s) => s.trim()),
    recurrence: recurrenceTypeSchema.default('none'),
    recurrenceDays: recurrenceDaysSchema,
    monthlyKind: monthlyKindSchema.nullable().default(null),
    recurrenceMonthDays: recurrenceMonthDaysSchema,
    recurrenceOrdinal: recurrenceOrdinalSchema,
    occurrenceDate: z.string().default(''),
    startDate: z.string().default(''),
    endDate: z.string().default(''),
    exclusionRanges: z.array(taskDateRangeSchema).max(100).default([]),
    requirePhoto: z.boolean().default(true),
    notifyEmployer: z.boolean().default(true),
    remindHelperBeforeDue: z.boolean().default(false),
    locale: z.enum(['en', 'zh-Hant', 'zh-Hans']).default('zh-Hant'),
});
const applyRecurrenceRefinements = (schema) => schema.superRefine((data, ctx) => {
    const d = data;
    refineWeekdaySelection(d, ctx);
    refineOccurrenceOrStartDates(d, ctx);
});
const titleOverrideSchema = z.string().max(100).transform((s) => s.trim()).optional();
export const pickupDropoffGroupCreateSchema = applyRecurrenceRefinements(scheduleFieldsSchema
    .extend({
    personName: z.string().min(1).max(120).transform((s) => s.trim()),
    location: z.string().max(150).default('').transform((s) => s.trim()),
    pickupEnabled: z.boolean().default(true),
    dropoffEnabled: z.boolean().default(true),
    pickupTime: optionalTimeSchema.default(''),
    dropoffTime: optionalTimeSchema.default(''),
    titleOverridePickup: titleOverrideSchema,
    titleOverrideDropoff: titleOverrideSchema,
})
    .refine((data) => data.pickupEnabled || data.dropoffEnabled, {
    message: 'At least one of pickup or drop-off must be enabled',
    path: ['pickupEnabled'],
})
    .refine((data) => {
    if (data.pickupEnabled && data.pickupTime.trim() === '') {
        return false;
    }
    return true;
}, { message: 'Pick-up time is required when pick-up is enabled', path: ['pickupTime'] })
    .refine((data) => {
    if (data.dropoffEnabled && data.dropoffTime.trim() === '') {
        return false;
    }
    return true;
}, { message: 'Drop-off time is required when drop-off is enabled', path: ['dropoffTime'] })
    .refine((data) => {
    if (!data.pickupEnabled || !data.dropoffEnabled) {
        return true;
    }
    return data.pickupTime.trim() !== data.dropoffTime.trim();
}, { message: 'Pick-up and drop-off times must be different', path: ['dropoffTime'] }));
export const medicationReminderGroupCreateSchema = applyRecurrenceRefinements(scheduleFieldsSchema
    .extend({
    personName: z.string().min(1).max(120).transform((s) => s.trim()),
    medicationTimes: z
        .array(optionalTimeSchema)
        .min(1)
        .max(MAX_MEDICATION_REMINDER_SLOTS)
        .refine((times) => {
        const normalized = times.map((t) => t.trim()).filter((t) => t !== '');
        return normalized.length > 0;
    }, { message: 'At least one medication time is required' })
        .refine((times) => {
        const normalized = times.map((t) => t.trim()).filter((t) => t !== '');
        return new Set(normalized).size === normalized.length;
    }, { message: 'Medication times must be unique' })
        .transform((times) => [...times].map((t) => t.trim()).filter((t) => t !== '').sort((a, b) => a.localeCompare(b))),
    sharedTitleOverride: titleOverrideSchema,
}));
export const pickupDropoffGroupUpdateSchema = pickupDropoffGroupCreateSchema;
export const medicationReminderGroupUpdateSchema = medicationReminderGroupCreateSchema;
//# sourceMappingURL=taskGroup.schema.js.map